import { API_BASE } from "@/constants/api";
import type { QuoteSessionEnrichment } from "@/api/types";
import type { UkAddressParts } from "@/utils/quoteMapper";

const SESSION_COOKIE = "helio_session_id";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

async function ensureSessionId(step: string, data: Record<string, unknown>): Promise<string | null> {
  const existing = getCookie(SESSION_COOKIE);
  if (existing) return existing;

  const res = await fetch(`${API_BASE}/api/quote-sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ last_step: step, data }),
  });
  if (!res.ok) return null;
  const { id } = await res.json() as { id: string };
  setCookie(SESSION_COOKIE, id, 30);
  return id;
}

function getSessionId() {
  return getCookie(SESSION_COOKIE);
}

async function fetchSession(id: string) {
  const res = await fetch(`${API_BASE}/api/quote-sessions/${id}`);
  if (res.status === 410) return { expired: true as const };
  if (!res.ok) return null;
  return res.json() as Promise<{ data: Record<string, unknown>; last_step: string; enrichment?: QuoteSessionEnrichment | null }>;
}

async function persist(step: string, data: Record<string, unknown>) {
  const id = getSessionId();
  if (!id) {
    const res = await fetch(`${API_BASE}/api/quote-sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ last_step: step, data }),
    });
    if (!res.ok) return;
    const { id: newId } = await res.json();
    setCookie(SESSION_COOKIE, newId, 30);
    return;
  }

  await fetch(`${API_BASE}/api/quote-sessions/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ last_step: step, data }),
  });
}

export function confirmAddressKey(fields: UkAddressParts): string {
  return `${fields.postcode.trim().toUpperCase()}|${fields.address1.trim().toLowerCase()}`;
}

type ConfirmAddressOptions = {
  lastStep?: string;
  data?: Record<string, unknown>;
  propertyId?: string;
  partnerId?: string;
  agencyName?: string;
};

async function postConfirmAddress(
  fields: UkAddressParts,
  options?: ConfirmAddressOptions,
): Promise<QuoteSessionEnrichment | null> {
  const sessionId = await ensureSessionId(options?.lastStep ?? "analysis", options?.data ?? {});
  if (!sessionId) return null;

  const res = await fetch(`${API_BASE}/api/quote-sessions/${sessionId}/confirm-address`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      postcode: fields.postcode,
      address1: fields.address1,
      address2: fields.address2 || null,
      city: fields.city || null,
      region: fields.region || null,
      udprn: fields.udprn ?? null,
      uprn: fields.uprn ?? null,
      last_step: options?.lastStep ?? "analysis",
      data: options?.data,
      ...(options?.propertyId ? { property_id: options.propertyId } : {}),
      ...(options?.partnerId ? { partner_id: options.partnerId } : {}),
      ...(options?.agencyName ? { agency_name: options.agencyName } : {}),
    }),
  });

  if (!res.ok) return null;
  const payload = await res.json() as { enrichment?: QuoteSessionEnrichment };
  return payload.enrichment ?? null;
}

const inflightConfirms = new Map<string, Promise<QuoteSessionEnrichment | null>>();

export async function confirmAddress(
  fields: UkAddressParts,
  options?: ConfirmAddressOptions,
): Promise<QuoteSessionEnrichment | null> {
  const key = confirmAddressKey(fields);
  const existing = inflightConfirms.get(key);
  if (existing) return existing;

  const promise = postConfirmAddress(fields, options).finally(() => {
    inflightConfirms.delete(key);
  });
  inflightConfirms.set(key, promise);
  return promise;
}

async function clearSession() {
  const id = getSessionId();
  if (id) {
    await fetch(`${API_BASE}/api/quote-sessions/${id}`, { method: "DELETE" });
  }
  deleteCookie(SESSION_COOKIE);
}

const quoteSessionApi = {
  getSessionId,
  fetchSession,
  persist,
  confirmAddress,
  clearSession,
};

export function useQuoteSession() {
  // Module-level API object keeps callback deps stable across renders.
  return quoteSessionApi;
}

export const STEP_FROM_ANALYTICS: Record<string, number> = {
  postcode: -2,
  roof_teaser: -1,
  bill: 0,
  usage: 1,
  address: 1,
  analysis: 1,
  system: 2,
  finance: 2,
  your_plan: 2,
  account: 3,
  results: 4,
};

export const ANALYTICS_FROM_STEP = [
  "bill", "analysis", "your_plan", "account", "results",
] as const;
